// ADMIN-5 (docs/specs/admin-5.md): org usage rollup + cost governance for admins.
// Nav/route gating lives in App.tsx (routes.ts's 'admin' view + Shell's is_admin-gated nav
// entry) — this component is defence-in-depth: a deep-linked /admin for a non-admin (or a
// stale-role race where the token's Scribe.Admin claim no longer matches a cached `Me`)
// renders the forbidden state below rather than firing any admin request.
//
// All money/state on screen comes straight from the server (`AdminUsage`/`OrgSettings`) —
// no client-side rate math, no `pricing.ts` import (decision, spec §1). This screen never
// requests or renders transcript content — counts/minutes/cost only.
import { useEffect, useId, useMemo, useState } from 'react';
import {
  useMe,
  useAdminUsage,
  useOrgSettings,
  useUpdateOrgSettings,
  useSetMemberBudget,
} from '../lib/queries';
import { formatUsd } from '../components/ui';
import { FeaturesCard } from '../components/FeaturesCard';
import { BudgetStateBadge, MembersTable } from '../components/MembersTable';
import { formatGb, formatMinutes, formatTokens } from '../lib/adminFormat';
import { ScribeApiError } from '@scribe/core/client';
import { OnDemandSavingsCard } from '../components/ondemand/OnDemandSavingsCard';
import type {
  Enforcement,
  OrgSettings,
  TranscriptionPolicy,
  UpdateOrgSettingsRequest,
} from '@scribe/core/types';

/** `YYYY-MM`, UTC — default month + the `<input type="month">`'s `max` (spec §4.1: "default
 * = current UTC month", "max = current month" — an admin can look back, never forward). */
function currentUtcMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** `true` if `error` is a 403 from an admin endpoint — the defense-in-depth trigger for the
 * forbidden state alongside `useMe().data?.is_admin === false` (spec §4 "States"). */
function isForbidden(error: unknown): boolean {
  return error instanceof ScribeApiError && error.status === 403;
}

export function AdminScreen() {
  const { data: me } = useMe();
  const isAdmin = me?.is_admin === true;
  const [month, setMonth] = useState(currentUtcMonth());

  // Gated on `me?.is_admin === true` — not just hidden output — per spec §4: an admin
  // query never fires for a non-admin/not-yet-resolved caller.
  const usage = useAdminUsage(month, isAdmin);
  const settingsQuery = useOrgSettings(isAdmin);
  const updateSettings = useUpdateOrgSettings();
  const setMemberBudget = useSetMemberBudget();

  const forbidden = (me != null && !isAdmin) || isForbidden(usage.error) || isForbidden(settingsQuery.error);

  if (forbidden) {
    return (
      <div className="card">
        <div className="card-head">
          <h1>Admin — usage &amp; cost</h1>
        </div>
        <p className="empty-state">Admins only — this area requires the Scribe.Admin role.</p>
      </div>
    );
  }

  const totals = usage.data?.totals;

  return (
    <div className="card">
      <div className="card-head">
        <h1>Admin — usage &amp; cost</h1>
        <div className="field admin-month-field">
          <span className="label">Month</span>
          <input
            type="month"
            aria-label="Month"
            value={month}
            max={currentUtcMonth()}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
      </div>

      {usage.error && !isForbidden(usage.error) && (
        <div className="banner banner-danger">
          <p>{(usage.error as Error).message || 'Failed to load usage.'}</p>
          <button className="sm" onClick={() => void usage.refetch()}>
            Retry
          </button>
        </div>
      )}
      {usage.isLoading && <p className="muted">Loading usage…</p>}

      {totals && (
        <div className="admin-summary">
          <SummaryCard label="Recordings" value={totals.recordings_count.toLocaleString()} />
          <SummaryCard
            label="Transcribed minutes"
            value={formatMinutes(totals.batch_seconds + totals.streaming_seconds)}
          />
          {/* TRIM-1: only once silence trimming has actually saved something — the server
              reports 0 while SCRIBE_TRIM_SILENCE is off, so this stays hidden until enabled. */}
          {(totals.trim_saved_seconds ?? 0) > 0 && (
            <SummaryCard label="Saved by trimming" value={formatMinutes(totals.trim_saved_seconds ?? 0)} />
          )}
          <SummaryCard label="Storage" value={formatGb(totals.storage_bytes)} />
          <SummaryCard label="AI tokens" value={formatTokens(totals.ai_tokens_in, totals.ai_tokens_out)} />
          <SummaryCard label="Estimated cost" value={formatUsd(totals.estimated_cost_usd)} />
          {/* LIVE-SAVE-1 (docs/specs/live-savings-1.md §5): only once there is something to show —
              the savers ship off, and a "$0.00 saved" card on every org would be noise. Net of
              the batch passes an auto swap induced, so it can read negative, honestly. */}
          {((totals.live_gated_seconds ?? 0) > 0 || (totals.live_auto_batch_seconds ?? 0) > 0) && (
            <SummaryCard
              label="Live savings (net)"
              value={`${formatUsd(totals.estimated_live_savings_usd ?? 0)} · ${formatMinutes(
                (totals.live_gated_seconds ?? 0) + (totals.live_auto_batch_seconds ?? 0),
              )} not streamed`}
            />
          )}
          <SummaryCard
            label="Org budget"
            value={totals.org_monthly_budget_usd != null ? formatUsd(totals.org_monthly_budget_usd) : 'Uncapped'}
            badge={<BudgetStateBadge state={totals.budget_state} />}
          />
        </div>
      )}

      {usage.data?.on_demand && <OnDemandSavingsCard savings={usage.data.on_demand} />}

      {usage.data && (
        <MembersTable
          members={usage.data.members}
          saving={setMemberBudget.isPending}
          onSaveBudget={(userId, monthlyBudgetUsd) => setMemberBudget.mutate({ userId, monthlyBudgetUsd })}
        />
      )}
      {setMemberBudget.isError && (
        <p className="error">{(setMemberBudget.error as Error).message}</p>
      )}

      <OrgSettingsCard
        settings={settingsQuery.data}
        onSave={(body) => updateSettings.mutate(body)}
        isPending={updateSettings.isPending}
        isError={updateSettings.isError}
        errorMessage={updateSettings.error instanceof Error ? updateSettings.error.message : undefined}
        isSuccess={updateSettings.isSuccess}
      />

      <FeaturesCard isAdmin={isAdmin} />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  badge,
}: {
  label: string;
  value: string;
  badge?: React.ReactNode;
}) {
  return (
    <div className="admin-summary-card">
      <span className="admin-summary-label">{label}</span>
      <span className="admin-summary-value">{value}</span>
      {badge}
    </div>
  );
}

const ENFORCEMENT_LABEL: Record<Enforcement, string> = {
  block: 'Hard-block at cap',
  warn: 'Warn only',
};

function parseUsdInput(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : Number(trimmed);
}

function OrgSettingsCard({
  settings,
  onSave,
  isPending,
  isError,
  errorMessage,
  isSuccess,
}: {
  settings: OrgSettings | undefined;
  onSave: (body: UpdateOrgSettingsRequest) => void;
  isPending: boolean;
  isError: boolean;
  errorMessage: string | undefined;
  isSuccess: boolean;
}) {
  const titleId = useId();
  const [orgBudget, setOrgBudget] = useState('');
  const [defaultBudget, setDefaultBudget] = useState('');
  const [enforcement, setEnforcement] = useState<Enforcement>('warn');
  const [warnPct, setWarnPct] = useState('80');
  // ONDEMAND-1: null/absent = transcribe-on-demand is off server-side — no control then.
  const [policyDefault, setPolicyDefault] = useState<TranscriptionPolicy | null>(null);

  // Re-syncs the draft fields from the server whenever `settings.updated_at` changes
  // (first load, or a successful save reflected back through the invalidated query) —
  // NOT on every render, so an in-flight edit isn't clobbered by an unrelated refetch that
  // returns the same, unchanged settings row.
  useEffect(() => {
    if (!settings) return;
    setOrgBudget(settings.org_monthly_budget_usd != null ? String(settings.org_monthly_budget_usd) : '');
    setDefaultBudget(
      settings.default_member_monthly_budget_usd != null ? String(settings.default_member_monthly_budget_usd) : '',
    );
    setEnforcement(settings.enforcement);
    setWarnPct(String(settings.warn_threshold_pct));
    setPolicyDefault(settings.transcription_policy_default ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.updated_at]);

  const dirty = useMemo(() => {
    if (!settings) return false;
    return (
      parseUsdInput(orgBudget) !== settings.org_monthly_budget_usd ||
      parseUsdInput(defaultBudget) !== settings.default_member_monthly_budget_usd ||
      enforcement !== settings.enforcement ||
      Number(warnPct) !== settings.warn_threshold_pct ||
      policyDefault !== (settings.transcription_policy_default ?? null)
    );
  }, [settings, orgBudget, defaultBudget, enforcement, warnPct, policyDefault]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!settings || !dirty) return;
    const body: UpdateOrgSettingsRequest = {};
    const newOrgBudget = parseUsdInput(orgBudget);
    if (newOrgBudget !== settings.org_monthly_budget_usd) body.org_monthly_budget_usd = newOrgBudget;
    const newDefaultBudget = parseUsdInput(defaultBudget);
    if (newDefaultBudget !== settings.default_member_monthly_budget_usd) {
      body.default_member_monthly_budget_usd = newDefaultBudget;
    }
    if (enforcement !== settings.enforcement) body.enforcement = enforcement;
    const newWarnPct = Number(warnPct);
    if (newWarnPct !== settings.warn_threshold_pct) body.warn_threshold_pct = newWarnPct;
    if (policyDefault && policyDefault !== settings.transcription_policy_default) {
      body.transcription_policy_default = policyDefault;
    }
    onSave(body);
  }

  return (
    <div className="row-secondary">
      <h2 id={titleId}>Org settings</h2>
      {!settings ? (
        <p className="muted">Loading settings…</p>
      ) : (
        <form onSubmit={submit} aria-labelledby={titleId}>
          <div className="admin-settings-grid">
            <div className="field">
              <span className="label">Org monthly budget (USD)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                placeholder="Uncapped"
                aria-label="Org monthly budget (USD)"
                value={orgBudget}
                onChange={(e) => setOrgBudget(e.target.value)}
              />
            </div>
            <div className="field">
              <span className="label">Default member budget (USD)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                placeholder="Uncapped"
                aria-label="Default member budget (USD)"
                value={defaultBudget}
                onChange={(e) => setDefaultBudget(e.target.value)}
              />
            </div>
            <div className="field">
              <span className="label">Enforcement</span>
              <select
                aria-label="Enforcement"
                value={enforcement}
                onChange={(e) => setEnforcement(e.target.value as Enforcement)}
              >
                <option value="block">{ENFORCEMENT_LABEL.block}</option>
                <option value="warn">{ENFORCEMENT_LABEL.warn}</option>
              </select>
            </div>
            <div className="field">
              <span className="label">Warn threshold (%)</span>
              <input
                type="number"
                min={1}
                max={100}
                aria-label="Warn threshold (%)"
                value={warnPct}
                onChange={(e) => setWarnPct(e.target.value)}
              />
            </div>
            {settings.transcription_policy_default != null && (
              <div className="field">
                <span className="label">Default transcription</span>
                <select
                  aria-label="Default transcription"
                  value={policyDefault ?? settings.transcription_policy_default}
                  onChange={(e) => setPolicyDefault(e.target.value as TranscriptionPolicy)}
                >
                  <option value="immediate">Transcribe automatically</option>
                  <option value="on_access">When opened, searched or asked about</option>
                </select>
                <p className="muted">
                  &ldquo;Transcribe automatically&rdquo; is the default. &ldquo;When opened&rdquo;
                  saves transcription cost, but recordings made in the Mac, iOS and Android apps
                  stay untranscribed until someone opens them on the web, searches for them or
                  reads their transcript. Tell your team before switching.
                </p>
              </div>
            )}
          </div>
          {isError && <p className="error">{errorMessage}</p>}
          {isSuccess && !isPending && <p className="muted">Settings saved.</p>}
          <div className="row">
            <button disabled={!dirty || isPending}>{isPending ? 'Saving…' : 'Save settings'}</button>
          </div>
        </form>
      )}
    </div>
  );
}
